/**
 * Table Rendering Logic Hook
 *
 * Manages rendering optimizations including column size CSS variables.
 */
import { useMemo, useEffect, useCallback } from "react";
import type { AppTable } from "../features";
import type { RowData } from "@tanstack/react-table";

export const useTableRendering = <T extends RowData>(
  table: AppTable<T>,
  metadata?: unknown,
  isLoading?: boolean,
  onColumnVisibilityChange?: (visible: boolean) => void,
  storageKey?: string,
  fitToContainer?: boolean,
) => {
  const hasMetadata = Boolean(metadata && !isLoading);

  const flatHeaders = table.getFlatHeaders();
  const columnSizing = table.store.state.columnSizing;
  const columnVisibility = table.store.state.columnVisibility;

  const columnSizeVars = useMemo(() => {
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < flatHeaders.length; i++) {
      const header = flatHeaders[i];
      const size = header.getSize();
      colSizes[`--header-${header.id}-size`] = size;
      colSizes[`--col-${header.column.id}-size`] = size;
    }
    return colSizes;
  }, [flatHeaders, columnSizing, columnVisibility]);

  const visibleColumns = table.getVisibleLeafColumns();
  const hasNoVisibleColumns = Boolean(storageKey && visibleColumns.length === 0);
  const areSomeColumnsVisible = visibleColumns.length > 0;

  useEffect(() => {
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(areSomeColumnsVisible);
    }
  }, [areSomeColumnsVisible, onColumnVisibilityChange]);

  const tableStyle = useCallback(() => ({
    tableLayout: "fixed" as const,
    width: fitToContainer ? "100%" : table.getTotalSize(),
    ...columnSizeVars,
  }), [table, columnSizeVars, fitToContainer]);

  return {
    hasMetadata,
    hasNoVisibleColumns,
    tableStyle,
    visibleColumns,
  };
};
